/**
 * Enriches /searchHotels rows (חבילות דינמיות) with future-cancellation metadata for filtering.
 * Does not mutate nested package objects; adds a hotel-level `_dynamicPackageFutureCxlMeta` field.
 */

import {
  DYNAMIC_PACKAGE_FUTURE_CXL_LOG_PREFIX,
  hasFutureCancellationPolicy,
} from "./hasFutureCancellationPolicy";

/**
 * @param {unknown} hotel
 * @returns {Generator<object>}
 */
function* iteratePackagesFromHotelSearchRow(hotel) {
  if (!hotel || typeof hotel !== "object") return;
  const h = hotel;

  const rooms = h.Rooms;
  if (Array.isArray(rooms)) {
    for (const room of rooms) {
      if (!room || typeof room !== "object") continue;
      const pkgs = room.packages ?? room.Packages;
      if (Array.isArray(pkgs)) {
        for (const pkg of pkgs) {
          if (pkg && typeof pkg === "object") yield pkg;
        }
      }
    }
  }

  const top = h.Packages ?? h.packages;
  if (Array.isArray(top)) {
    for (const pkg of top) {
      if (pkg && typeof pkg === "object") yield pkg;
    }
  }

  const synthetic = {};
  let any = false;
  const lu =
    h.LowestPackageRefundableUntil ??
    h.lowestPackageRefundableUntil ??
    h.RefundableUntil ??
    h.refundableUntil;
  if (lu != null && lu !== "") {
    synthetic.RefundableUntil = lu;
    any = true;
  }
  const rf =
    h.LowestPackageRefundability ??
    h.lowestPackageRefundability ??
    h.Refundability ??
    h.refundability;
  if (rf !== undefined && rf !== null && rf !== "") {
    synthetic.Refundability = rf;
    any = true;
  }
  const cp = h.CancellationPolicy ?? h.cancellation ?? h.FreeCancellation ?? h.freeCancellation;
  if (cp != null && cp !== "") {
    synthetic.CancellationPolicy = cp;
    any = true;
  }
  if (any) yield synthetic;
}

/**
 * @param {unknown[]} hotels
 * @param {{ now?: Date }} [opts]
 * @returns {unknown[]}
 */
export function attachDynamicPackageFutureCxlToHotelSearchResults(hotels, opts = {}) {
  if (!Array.isArray(hotels) || hotels.length === 0) return hotels;
  const now = opts.now ?? new Date();
  let warnBudget = 3;

  return hotels.map((hotel) => {
    if (!hotel || typeof hotel !== "object") return hotel;
    let pkgCount = 0;
    let anyFuture = false;
    let anyPolicySignal = false;

    for (const pkg of iteratePackagesFromHotelSearchRow(hotel)) {
      pkgCount += 1;
      const r = hasFutureCancellationPolicy(pkg, now);
      if (r.reason !== "no_policy") anyPolicySignal = true;
      if (r.hasFutureCxl) anyFuture = true;
    }

    const hid = hotel.ID ?? hotel.Id ?? hotel.id ?? "?";

    // Cap console spam on ~2k hotel lists — full logs only with HR_DP_PERF_DEBUG.
    const verboseCxl =
      typeof window !== "undefined" &&
      window.localStorage?.getItem("HR_DP_PERF_DEBUG") === "1";
    if (verboseCxl || warnBudget > 0) {
      if (pkgCount === 0) {
        if (!verboseCxl) warnBudget -= 1;
        console.warn(
          `${DYNAMIC_PACKAGE_FUTURE_CXL_LOG_PREFIX} search_results: no package rows to inspect for cancellation policy (hotel ${hid}). List-stage future-CXL filter will not exclude this hotel (unknown); verify on profile.`
        );
      } else if (!anyPolicySignal) {
        if (!verboseCxl) warnBudget -= 1;
        console.warn(
          `${DYNAMIC_PACKAGE_FUTURE_CXL_LOG_PREFIX} search_results: packages present but no RefundableUntil / cancellation deadline fields on list payload (hotel ${hid}).`
        );
      }
    }

    const listPolicyIncomplete = pkgCount === 0;

    return {
      ...hotel,
      _dynamicPackageFutureCxlMeta: {
        hasAnyFutureCxlRoom: listPolicyIncomplete ? null : anyFuture,
        packagesScanned: pkgCount,
        policySignalsPresent: anyPolicySignal,
        listPolicyIncomplete,
      },
    };
  });
}
